import { State } from '../State';
import {
  getAccessoryItemTier,
  getAccessoryTierByNumber,
} from '../accessory/accessory.getters';
import { accessoryItems } from '../accessory/accessory.ruleset';
import { AnalyticsEvent } from '../analytics';
import { getDuplicateItems } from '../catalog/catalog.getters';
import { Currency } from '../data/Gachas.schema';
import {
  getDecorationItemTier,
  getDecorationTierByNumber,
} from '../decoration/decoration.getters';
import { decorationItems } from '../decoration/decoration.ruleset';
import { isPromoReady } from '../promo/promo.getters';
import { assertNever } from '../utils';
import { getRandomItemFromArray, weightedRandom } from '../utils/random';
import {
  Gacha,
  GachaBucketID,
  GachaID,
  GachaItem,
  GachaTag,
  gachaBucketsById,
  gachaEventSchedule,
  gachas,
  gachasById,
} from './gacha.ruleset';

/**
 * Get the current gacha event if there is one.
 * @param state
 * @param now
 * @returns
 */
export function getCurrentGachaEvent(_: State, now: number) {
  const currentEvent = gachaEventSchedule.getCurrentEvent(now);
  return currentEvent;
}

/**
 * Get a list of available gachas based on the current event.
 * @param state
 * @param now
 * @returns
 */
export function getAvailableGachas(state: State, now: number): Gacha[] {
  const tag = getCurrentGachaEvent(state, now)?.metadata?.tag;

  // If there is no current event, return an empty list.
  if (!tag) {
    return [];
  }

  return gachas.filter((x) => {
    // Only show gachas that match the current event tag.
    if (!x.tags?.includes(tag)) return false;

    // Only show discounted gachas if they are available.
    if (x.tags?.includes('discount')) {
      // Must not be on cooldown.
      // The 1-spin and 10 spin discount are independent now, so each spin type should have its own cooldown.
      if (gachaHasTag(state, x.id, '10-spin')) {
        return isPromoReady(state, 'dailyGachaMultiSpin', now);
      } else {
        return isPromoReady(state, 'dailyGacha', now);
      }
    }

    // Show all other gachas.
    return true;
  });
}

/**
 * Roll gacha.
 * @param gachaId
 * @param random
 * @returns
 */
export function rollGacha(
  state: State,
  gachaId: GachaID,
  random: () => number,
  now: number,
): { items: GachaItem[]; events: AnalyticsEvent[] } {
  // Tracking events
  const events: AnalyticsEvent[] = [];

  // Items that will be granted to user.
  const items: GachaItem[] = [];

  // Roll gacha for each spin.
  for (let i = 0; i < getGachaSpins(state, gachaId); i++) {
    // Get random bucket.
    const buckets = getGachaBuckets(state, gachaId);
    const bucketRoll = weightedRandom(Object.values(buckets), random);
    const bucketId = Object.keys(buckets)[bucketRoll] as GachaBucketID;

    // Get random items from bucket.
    const bucketItems = getGachaBucketItems(bucketId);

    // Get random item from bucket.
    const item = getRandomItemFromArray(bucketItems, random);

    // Push item.
    items.push(item);

    // Track GachaResult event.
    const type = item.type;

    switch (type) {
      case 'decoration':
      case 'accessory':
        events.push({
          eventType: 'GachaResult',
          eventProperties: {
            gachaID: gachaId,
            gachaBucketID: bucketId,
            itemID: item.id,
            itemTierID: item.tierId,
            tier: item.tier,
            numItems: getGachaItemCount(state, item),
            timestamp: new Date(now).toISOString(),
          },
        });
        break;

      default:
        assertNever(type);
    }
  }

  return { items, events };
}

/**
 *  Get gacha spins.
 */
function getGachaSpins(state: State, id: GachaID): number {
  return gachasById[id].num_spins;
}

/**
 * Get gacha buckets weights for a specific gacha.
 */
function getGachaBuckets(state: State, id: GachaID) {
  return gachasById[id].buckets;
}

/**
 * Filter items by tags.
 * @param items
 * @param includeTags
 * @param excludeTags
 * @returns
 */
function filterItemsByTags<T extends { tags?: string[] }>(
  items: readonly T[],
  includeTags: readonly string[],
  excludeTags: readonly string[],
): T[] {
  return items.filter((item) => {
    // Check if the item has at least one of the include tags. If `includeTags` is empty, then include all items. If item has no tags, then don't include it.
    const include = includeTags?.length
      ? (item.tags?.some((x) => includeTags?.includes(x) ?? true) ?? false)
      : true;

    // Check if the item has at least one of the exclude tags. If `excludeTags` is empty, then don't exclude any items. If item has no tags, then include it.
    const exclude = excludeTags?.length
      ? (item.tags?.some((x) => excludeTags?.includes(x) ?? false) ?? false)
      : false;

    return include && !exclude;
  });
}

/**
 * Get gacha bucket items.
 */
function getGachaBucketItems(id: GachaBucketID): GachaItem[] {
  const bucket = gachaBucketsById[id];

  const type = bucket.item_type;
  switch (type) {
    case 'decoration':
      return filterItemsByTags(
        decorationItems,
        bucket.tags_include ?? [],
        bucket.tags_exclude ?? [],
      )
        .filter((item) => !!getDecorationTierByNumber(item.id, bucket.tier)) // Make sure tier exists for item.
        .map((item) => {
          const tier = getDecorationItemTier(
            item.id,
            getDecorationTierByNumber(item.id, bucket.tier)!.id,
          );

          return {
            type,
            tier: bucket.tier,
            id: item.id,
            image: tier.image!,
            tierId: tier.id,
            name_en: item.name_en,
            name_jp: item.name_jp,
            theme: item.theme,
            room: item.room,
          };
        });

    case 'accessory': {
      return filterItemsByTags(
        accessoryItems,
        bucket.tags_include ?? [],
        bucket.tags_exclude ?? [],
      )
        .filter((item) => !!getAccessoryTierByNumber(item.id, bucket.tier)) // Make sure tier exists for item.
        .map((item) => {
          const tier = getAccessoryItemTier(
            item.id,
            getAccessoryTierByNumber(item.id, bucket.tier)!.id,
          );

          return {
            type,
            tier: bucket.tier,
            id: item.id,
            image: tier.image!,
            tierId: tier.id,
            name_en: item.name_en,
            name_jp: item.name_jp,
            theme: item.theme,
          };
        });
    }

    default:
      assertNever(type);
  }
}

/**
 * Get amount of items that the player has in their inventory.
 * @param state
 * @param item
 */
function getGachaItemCount(state: State, item: GachaItem) {
  switch (item.type) {
    case 'decoration':
    case 'accessory':
      return getDuplicateItems(state, item.id, item.tierId);

    default:
      assertNever(item);
  }
}

/**
 * Get gacha price.
 */
export function getGachaPrice(state: State, id: GachaID): number {
  return gachasById[id].price;
}

/**
 * Get gacha currency.
 */
export function getGachaCurrency(id: GachaID): Currency {
  return gachasById[id].currency;
}

/**
 * Check if gacha has a tag.
 */
export function gachaHasTag(state: State, id: GachaID, tag: GachaTag): boolean {
  return !!gachasById[id].tags?.includes(tag);
}
